import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { BackArrowIcon } from 'components/elements/Icons';
import { Alert } from 'components/elements';
import { useFetchInboundMessagesDetails, useDeviceDetect, useMessagesActions } from 'hooks';
import { dateUtils } from 'utils';
import ChatMessageSendForm from './ChatMessageSendForm';
import ChatMessages from './ChatMessages';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function ChatThread({ selectedConversationId, onClickBack, accountId }) {
  const classes = useStyles();
  const { isDesktop } = useDeviceDetect();
  const { t } = useTranslation('labels');
  const { data: chatMessages, participants, meta, isLoading } = useFetchInboundMessagesDetails(selectedConversationId);

  const conversationParticipants = useMemo(
    () =>
      uniqBy(participants, 'id').map(({ salutation, firstName, lastName, email, role }, i) =>
        role === 'guest_user' ? email : `${salutation.length ? `${salutation}.` : ''} ${firstName} ${lastName}${participants.length - 1 === i ? '' : ', '}`,
      ),
    [participants],
  );

  const initialMessageId = chatMessages[0]?.id;
  const { reply, download, downloadingList } = useMessagesActions(selectedConversationId, initialMessageId);
  const isActive = !!meta?.attributes?.isActive;
  const endedAt = dateUtils.fullFormatDateTimeString(meta?.attributes?.endedAt);

  return (
    <div className={classes.chatThread} data-testid="chat-thread">
      {!isLoading && selectedConversationId ? (
        <>
          <div className={classes.header}>
            {!isDesktop && (
              <button className={classes.backButton} type="button" onClick={onClickBack}>
                <BackArrowIcon />
              </button>
            )}
            <Typography className={classes.title} variant="h5" component="div" noWrap>
              {conversationParticipants}
            </Typography>
          </div>
          <Alert text={t('please_note_that_the_clinic_staff_is_unable_to_respond_immediately')} />
          <div className={classes.lineWrapper}>
            <div className={classes.thread}>
              <ChatMessages messages={chatMessages} participants={participants} onDownload={download} downloadingList={downloadingList} accountId={accountId} />
            </div>
          </div>
          {isActive ? (
            <ChatMessageSendForm onReply={reply} allowAttachments />
          ) : (
            <Typography className={classes.chatEndedMessage} variant="subtitle1" component="span">
              {`${t('chat_ended_on')} ${endedAt}`}
            </Typography>
          )}
        </>
      ) : (
        <div className={classes.caption}>
          <Typography variant="subtitle1" component="span">
            {isLoading ? t('loading') : t('no_chats_available')}
          </Typography>
        </div>
      )}
    </div>
  );
}

ChatThread.propTypes = {
  selectedConversationId: PropTypes.string,
  onClickBack: PropTypes.func.isRequired,
  accountId: PropTypes.string,
};

ChatThread.defaultProps = {
  selectedConversationId: null,
  accountId: '',
};

export default ChatThread;
